/**
 * Die Texterkennung: PaddleOCR-Modelle auf der ONNX-Laufzeit (ADR 0033).
 *
 * Drei Modelle hintereinander:
 * 1. **Suche** -- wo im Bild steht Schrift? Ergibt eine Wahrscheinlichkeits-
 *    karte, daraus werden gedrehte Kaesten um die Zeilen (`kaesten.ts`).
 * 2. **Richtung** -- steht die ausgeschnittene Zeile auf dem Kopf?
 * 3. **Lesen** -- welche Zeichen stehen in der Zeile?
 *
 * Die Laufzeit wird hereingereicht statt importiert: Im Worker ist es die
 * WebAssembly-Fassung fuer den Browser, im Messlauf die fuer Node. Die
 * Rechnung dazwischen ist dieselbe.
 */

import type * as Ort from "onnxruntime-web";
import { ctcEntschluesseln } from "./ctc.js";
import { ecken, kaestenFinden, kastenAlsRahmen, KASTEN_VOREINSTELLUNG } from "./kaesten.js";
import { inventarnummernIn } from "./nummern.js";
import {
  alsEingabe,
  ausschneiden,
  drehen,
  kontrastVerstaerken,
  skalieren,
  type Punkt,
  type Rohbild,
} from "./rohbild.js";

type Laufzeit = Pick<typeof Ort, "InferenceSession" | "Tensor">;

export interface Modelle {
  suche: Uint8Array;
  richtung: Uint8Array;
  lesen: Uint8Array;
  /** Das Woerterbuch des Lesemodells, ein Zeichen je Zeile. */
  woerter: string;
}

/**
 * Was versucht wird, wenn der erste Durchgang keine Nummer findet.
 *
 * Gemessen (ADR 0033): Der erste Durchgang allein las alle Probefotos, auch
 * den schraegen Schrumpfschlauch -- die Zeilen werden einzeln geradegerueckt.
 * Kontrast vorab verschlechterte ein Foto. Beides bleibt deshalb Nachfassen,
 * nicht Vorbereitung.
 */
export interface LeseOptionen {
  /** Noch einmal mit Graustufen und steilem Kontrast. */
  kontrastNachfassen: boolean;
  /** Noch einmal um ±25° gedreht. */
  schraegNachfassen: boolean;
}

export const LESEN_VOREINSTELLUNG: LeseOptionen = {
  kontrastNachfassen: true,
  schraegNachfassen: false,
};

export interface Zeile {
  text: string;
  sicherheit: number;
  /** Ecken im gelesenen Bild, zum Einzeichnen. */
  ecken: [Punkt, Punkt, Punkt, Punkt];
  /** Um wie viel Grad das Bild fuer diesen Versuch gedreht war. */
  drehung: number;
}

export interface Leseergebnis {
  zeilen: Zeile[];
  /** Alle Inventarnummern, die in den Zeilen stehen -- ob im Bestand oder nicht. */
  nummern: string[];
  ms: { suche: number; lesen: number; gesamt: number };
  /** Wie viele Durchgaenge (Drehungen) es gebraucht hat. */
  durchgaenge: number;
}

/** Laengste Seite fuer die Suche. PaddleOCR nimmt 960; mehr kostet nur Zeit. */
const SUCHE_HOECHSTENS = 960;
/** Hoehe einer Zeile fuer das Lesemodell. */
const ZEILENHOEHE = 48;
/** Breite, auf die das Richtungsmodell eine Zeile auffuellt. */
const RICHTUNG_BREITE = 192;
/** Ab dieser Wahrscheinlichkeit gilt eine Zeile als kopfueber. */
const KOPFUEBER_AB = 0.9;
/** Zeilen, die das Modell selbst kaum glaubt, zaehlen nicht. */
const ZEILE_MINDESTENS = 0.5;
/** Laenger liest keiner auf einem Schild; begrenzt die Rechenzeit. */
const ZEILE_HOECHSTENS_BREIT = 1600;

const IMAGENET_MITTEL = [0.485, 0.456, 0.406] as const;
const IMAGENET_STREUUNG = [0.229, 0.224, 0.225] as const;
const HALB = [0.5, 0.5, 0.5] as const;

export class Texterkenner {
  readonly #ort: Laufzeit;
  readonly #suche: Ort.InferenceSession;
  readonly #richtung: Ort.InferenceSession;
  readonly #lesen: Ort.InferenceSession;
  readonly #zeichen: string[];

  private constructor(
    ort: Laufzeit,
    suche: Ort.InferenceSession,
    richtung: Ort.InferenceSession,
    lesen: Ort.InferenceSession,
    zeichen: string[],
  ) {
    this.#ort = ort;
    this.#suche = suche;
    this.#richtung = richtung;
    this.#lesen = lesen;
    this.#zeichen = zeichen;
  }

  static async erstellen(ort: Laufzeit, modelle: Modelle): Promise<Texterkenner> {
    const optionen: Ort.InferenceSession.SessionOptions = {
      executionProviders: ["wasm"],
      graphOptimizationLevel: "all",
    };
    const [suche, richtung, lesen] = await Promise.all([
      ort.InferenceSession.create(modelle.suche, optionen),
      ort.InferenceSession.create(modelle.richtung, optionen),
      ort.InferenceSession.create(modelle.lesen, optionen),
    ]);
    // Ein Zeichen je Zeile, und wie bei PaddleOCR ein Leerzeichen als letzte
    // Klasse. Die abschliessende Leerzeile der Datei ist kein Zeichen.
    const zeichen = modelle.woerter.replace(/\r/g, "").split("\n");
    if (zeichen.at(-1) === "") zeichen.pop();
    zeichen.push(" ");
    return new Texterkenner(ort, suche, richtung, lesen, zeichen);
  }

  /**
   * Liest ein Bild: zuerst so, wie es ist, danach -- nur solange keine
   * Nummer gefunden ist -- mit den Nachfass-Versuchen aus `optionen`.
   */
  async lesen(bild: Rohbild, optionen: LeseOptionen = LESEN_VOREINSTELLUNG): Promise<Leseergebnis> {
    const start = performance.now();
    const ms = { suche: 0, lesen: 0, gesamt: 0 };
    const versuche: { kontrast: boolean; drehung: number }[] = [{ kontrast: false, drehung: 0 }];
    if (optionen.kontrastNachfassen) versuche.push({ kontrast: true, drehung: 0 });
    if (optionen.schraegNachfassen)
      versuche.push({ kontrast: false, drehung: 25 }, { kontrast: false, drehung: -25 });

    let zeilen: Zeile[] = [];
    let nummern: string[] = [];
    let durchgaenge = 0;
    let kontrastbild: Rohbild | null = null;
    for (const { kontrast, drehung } of versuche) {
      durchgaenge++;
      if (kontrast) kontrastbild ??= kontrastVerstaerken(bild);
      const gedreht = drehen(kontrast && kontrastbild !== null ? kontrastbild : bild, drehung);
      const neue = await this.#durchgang(gedreht, drehung, ms);
      zeilen = zeilen.concat(neue);
      nummern = [...new Set(zeilen.flatMap((z) => inventarnummernIn(z.text)))];
      if (nummern.length > 0) break;
    }
    ms.gesamt = performance.now() - start;
    return { zeilen, nummern, ms, durchgaenge };
  }

  async #durchgang(bild: Rohbild, drehung: number, ms: Leseergebnis["ms"]): Promise<Zeile[]> {
    // --- Suche ---
    const t0 = performance.now();
    const massstab = Math.min(1, SUCHE_HOECHSTENS / Math.max(bild.breite, bild.hoehe));
    const kb = Math.max(32, Math.ceil((bild.breite * massstab) / 32) * 32);
    const kh = Math.max(32, Math.ceil((bild.hoehe * massstab) / 32) * 32);
    const klein = skalieren(bild, kb, kh);
    const eingabe = new this.#ort.Tensor(
      "float32",
      alsEingabe(klein, IMAGENET_MITTEL, IMAGENET_STREUUNG),
      [1, 3, kh, kb],
    );
    const karte = (await ausfuehren(this.#suche, eingabe)).data as Float32Array;
    const kaesten = kaestenFinden(karte, kb, kh, KASTEN_VOREINSTELLUNG);
    ms.suche += performance.now() - t0;

    // --- Richtung und Lesen, Zeile fuer Zeile ---
    const t1 = performance.now();
    const zeilen: Zeile[] = [];
    for (const kasten of kaesten) {
      const rahmen = kastenAlsRahmen(kasten, bild.breite / kb, bild.hoehe / kh);
      const laenge = Math.hypot(rahmen.u[0], rahmen.u[1]);
      const hoehe = Math.hypot(rahmen.v[0], rahmen.v[1]);
      if (hoehe < 4) continue;
      const breite = Math.min(
        ZEILE_HOECHSTENS_BREIT,
        Math.max(ZEILENHOEHE / 2, Math.round((ZEILENHOEHE * laenge) / hoehe)),
      );
      let zeile = ausschneiden(bild, rahmen, breite, ZEILENHOEHE);
      if (await this.#kopfueber(zeile)) zeile = drehen(zeile, 180);
      const gelesen = await this.#zeileLesen(zeile);
      if (gelesen.sicherheit < ZEILE_MINDESTENS || gelesen.text.trim() === "") continue;
      zeilen.push({ ...gelesen, ecken: ecken(rahmen), drehung });
    }
    ms.lesen += performance.now() - t1;
    return zeilen;
  }

  async #kopfueber(zeile: Rohbild): Promise<boolean> {
    const breite = Math.min(RICHTUNG_BREITE, zeile.breite);
    const passend = zeile.breite === breite ? zeile : skalieren(zeile, breite, ZEILENHOEHE);
    const daten = alsEingabe(passend, HALB, HALB, RICHTUNG_BREITE);
    const eingabe = new this.#ort.Tensor("float32", daten, [1, 3, ZEILENHOEHE, RICHTUNG_BREITE]);
    const p = (await ausfuehren(this.#richtung, eingabe)).data as Float32Array;
    return (p[1] ?? 0) > KOPFUEBER_AB;
  }

  async #zeileLesen(zeile: Rohbild): Promise<{ text: string; sicherheit: number }> {
    const eingabe = new this.#ort.Tensor("float32", alsEingabe(zeile, HALB, HALB), [
      1,
      3,
      ZEILENHOEHE,
      zeile.breite,
    ]);
    const t = await ausfuehren(this.#lesen, eingabe);
    const [, schritte = 0, klassen = 0] = t.dims;
    return ctcEntschluesseln(t.data as Float32Array, schritte, klassen, this.#zeichen);
  }
}

/** Ein Modell mit genau einer Ein- und einer Ausgabe rechnen lassen. */
async function ausfuehren(sitzung: Ort.InferenceSession, eingabe: Ort.Tensor): Promise<Ort.Tensor> {
  const [rein] = sitzung.inputNames;
  const [raus] = sitzung.outputNames;
  if (rein === undefined || raus === undefined) throw new Error("Modell ohne Ein- oder Ausgabe");
  const ergebnis = (await sitzung.run({ [rein]: eingabe }))[raus];
  if (ergebnis === undefined) throw new Error(`Modell lieferte keine Ausgabe „${raus}“`);
  return ergebnis;
}
